/**
 * WaitlistLanding - iOS App Waitlist Signup
 *
 * Clean, Apple-style form for collecting waitlist signups.
 * Captures: email, use case, industry, and optional details.
 *
 * @see DESIGN_SYSTEM.md
 */

import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  submitWaitlistEntry,
  checkWaitlistEmail,
  USE_CASE_OPTIONS,
  INDUSTRY_OPTIONS,
  TEAM_SIZE_OPTIONS,
  ROLE_OPTIONS
} from '../services/waitlistService';

export default function WaitlistLanding() {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();

  const [formData, setFormData] = useState({
    firstName: userProfile?.firstName || '',
    lastName: userProfile?.lastName || '',
    email: user?.email || '',
    usageType: '', // personal or professional
    useCase: '',
    industry: '',
    notes: '',
    companyName: '',
    teamSize: '',
    role: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  // Update form when userProfile loads (async)
  useEffect(() => {
    if (userProfile) {
      setFormData(prev => ({
        ...prev,
        firstName: prev.firstName || userProfile.firstName || '',
        lastName: prev.lastName || userProfile.lastName || ''
      }));
    }
  }, [userProfile]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      // Validate required fields
      if (!formData.firstName || !formData.lastName || !formData.email || !formData.usageType || !formData.useCase || !formData.industry) {
        throw new Error('Please fill in all required fields');
      }

      // Check if email is already on waitlist (only works for authenticated users)
      const existing = await checkWaitlistEmail(formData.email, !!user);
      if (existing.exists) {
        if (existing.status === 'approved') {
          // User is approved, redirect to login
          navigate('/login');
          return;
        }
        throw new Error('This email is already on the waitlist');
      }

      // Submit entry
      await submitWaitlistEntry(formData, user?.uid || null);

      // Mark as joined and save email for status lookup
      localStorage.setItem('waitlist_joined', 'true');
      localStorage.setItem('waitlist_email', formData.email);
      setIsSuccess(true);
    } catch (err) {
      console.error('Waitlist submission error:', err);
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success state
  if (isSuccess) {
    return (
      <div className="min-h-screen bg-[#000000] flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center">
          {/* Success icon */}
          <div className="w-20 h-20 mx-auto mb-8 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>

          <h1 className="text-3xl font-bold text-white mb-4">
            You're on the list
          </h1>

          <p className="text-white/60 text-lg mb-8 leading-relaxed">
            Thanks for joining the QuickMeet iOS waitlist. We're reviewing access in waves and will notify you when it's your turn.
          </p>

          <div className="p-6 rounded-2xl bg-white/5 border border-white/10 mb-8">
            <h3 className="text-white font-medium mb-2">What happens next?</h3>
            <ul className="text-white/60 text-sm space-y-2 text-left">
              <li className="flex items-start gap-2">
                <span className="text-[#06c] mt-1">1.</span>
                We review your submission
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#06c] mt-1">2.</span>
                You'll receive an email when approved
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#06c] mt-1">3.</span>
                Download the app and sign in
              </li>
            </ul>
          </div>

          <button
            onClick={() => navigate('/')}
            className="text-[#06c] hover:text-[#0a84ff] transition-colors font-medium"
          >
            Back to home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#000000]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2"
          >
            <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            <span className="text-white font-semibold text-lg">QuickMeet</span>
          </button>

          <Link
            to="/login"
            className="text-white/60 hover:text-white text-sm font-medium transition-colors"
          >
            Sign in
          </Link>
        </div>
      </header>

      {/* Main content */}
      <main className="pt-24 pb-16 px-6">
        <div className="max-w-lg mx-auto">
          {/* Hero */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-6">
              <div className="w-2 h-2 bg-[#06c] rounded-full animate-pulse" />
              <span className="text-white/80 text-sm font-medium">iOS App Coming Soon</span>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
              Join the QuickMeet
              <br />
              <span className="bg-gradient-to-r from-[#06c] to-[#5856d6] bg-clip-text text-transparent">
                Waitlist
              </span>
            </h1>

            <p className="text-white/60 text-lg max-w-md mx-auto">
              We're rolling out access in waves. Tell us how you plan to use QuickMeet.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-white/80 text-sm font-medium mb-2">
                  First name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder="John"
                  required
                  className="w-full px-4 py-3.5 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-[#06c] focus:ring-1 focus:ring-[#06c] transition-colors"
                />
              </div>
              <div>
                <label className="block text-white/80 text-sm font-medium mb-2">
                  Last name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder="Doe"
                  required
                  className="w-full px-4 py-3.5 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-[#06c] focus:ring-1 focus:ring-[#06c] transition-colors"
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-white/80 text-sm font-medium mb-2">
                Email address <span className="text-red-400">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@company.com"
                required
                disabled={!!user?.email}
                className="w-full px-4 py-3.5 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-[#06c] focus:ring-1 focus:ring-[#06c] transition-colors disabled:opacity-50"
              />
            </div>

            {/* Usage Type - Personal or Professional */}
            <div>
              <label className="block text-white/80 text-sm font-medium mb-2">
                How will you be using QuickMeet? <span className="text-red-400">*</span>
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, usageType: 'personal' }))}
                  className={`px-4 py-3.5 rounded-xl border text-left transition-all ${
                    formData.usageType === 'personal'
                      ? 'bg-[#06c]/20 border-[#06c] text-white'
                      : 'bg-white/5 border-white/10 text-white/60 hover:border-white/20'
                  }`}
                >
                  <div className="font-medium">Personal</div>
                  <div className="text-xs opacity-60">Friends, family, hobby</div>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, usageType: 'professional' }))}
                  className={`px-4 py-3.5 rounded-xl border text-left transition-all ${
                    formData.usageType === 'professional'
                      ? 'bg-[#06c]/20 border-[#06c] text-white'
                      : 'bg-white/5 border-white/10 text-white/60 hover:border-white/20'
                  }`}
                >
                  <div className="font-medium">Professional</div>
                  <div className="text-xs opacity-60">Work, business, team</div>
                </button>
              </div>
            </div>

            {/* Use Case */}
            <div>
              <label className="block text-white/80 text-sm font-medium mb-2">
                How will you use QuickMeet? <span className="text-red-400">*</span>
              </label>
              <select
                name="useCase"
                value={formData.useCase}
                onChange={handleChange}
                required
                className="w-full px-4 py-3.5 rounded-xl bg-white/5 border border-white/10 text-white focus:outline-none focus:border-[#06c] focus:ring-1 focus:ring-[#06c] transition-colors appearance-none cursor-pointer"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='white' stroke-opacity='0.5'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 1rem center',
                  backgroundSize: '1.25rem'
                }}
              >
                <option value="" className="bg-[#1c1c1e]">Select a use case</option>
                {USE_CASE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value} className="bg-[#1c1c1e]">
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Industry */}
            <div>
              <label className="block text-white/80 text-sm font-medium mb-2">
                Industry <span className="text-red-400">*</span>
              </label>
              <select
                name="industry"
                value={formData.industry}
                onChange={handleChange}
                required
                className="w-full px-4 py-3.5 rounded-xl bg-white/5 border border-white/10 text-white focus:outline-none focus:border-[#06c] focus:ring-1 focus:ring-[#06c] transition-colors appearance-none cursor-pointer"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='white' stroke-opacity='0.5'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 1rem center',
                  backgroundSize: '1.25rem'
                }}
              >
                <option value="" className="bg-[#1c1c1e]">Select your industry</option>
                {INDUSTRY_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value} className="bg-[#1c1c1e]">
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Company Name - Optional, visible */}
            <div>
              <label className="block text-white/80 text-sm font-medium mb-2">
                Company / Organization
                <span className="text-white/40 ml-1">(optional)</span>
              </label>
              <input
                type="text"
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
                placeholder="Acme Inc."
                className="w-full px-4 py-3.5 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-[#06c] focus:ring-1 focus:ring-[#06c] transition-colors"
              />
            </div>

            {/* Team Size - Optional, visible */}
            <div>
              <label className="block text-white/80 text-sm font-medium mb-2">
                Team size
                <span className="text-white/40 ml-1">(optional)</span>
              </label>
              <select
                name="teamSize"
                value={formData.teamSize}
                onChange={handleChange}
                className="w-full px-4 py-3.5 rounded-xl bg-white/5 border border-white/10 text-white focus:outline-none focus:border-[#06c] focus:ring-1 focus:ring-[#06c] transition-colors appearance-none cursor-pointer"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='white' stroke-opacity='0.5'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 1rem center',
                  backgroundSize: '1.25rem'
                }}
              >
                <option value="" className="bg-[#1c1c1e]">Select team size</option>
                {TEAM_SIZE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value} className="bg-[#1c1c1e]">
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Notes - Optional */}
            <div>
              <label className="block text-white/80 text-sm font-medium mb-2">
                Anything else you'd like us to know?
                <span className="text-white/40 ml-1">(optional)</span>
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="Tell us more about your needs..."
                rows={3}
                className="w-full px-4 py-3.5 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-[#06c] focus:ring-1 focus:ring-[#06c] transition-colors resize-none"
              />
            </div>

            {/* Error message */}
            {error && (
              <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                {error}
              </div>
            )}

            {/* Submit button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-4 rounded-xl bg-gradient-to-r from-[#06c] to-[#0055d4] text-white font-semibold text-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Joining...
                </>
              ) : (
                'Join Waitlist'
              )}
            </button>

            {/* Privacy note */}
            <p className="text-white/40 text-xs text-center">
              By joining, you agree to our{' '}
              <Link to="/privacy" className="text-white/60 hover:text-white underline">
                Privacy Policy
              </Link>
              {' '}and{' '}
              <Link to="/terms" className="text-white/60 hover:text-white underline">
                Terms of Service
              </Link>
            </p>
          </form>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 px-6">
        <div className="max-w-lg mx-auto text-center">
          <p className="text-white/40 text-sm">
            QuickMeet by TheJhyeFactor
          </p>
        </div>
      </footer>
    </div>
  );
}
